/**
 * Conversion functions matching the data scientist's approach
 */
export function toLogSpace(pressure: number): number {
    return Math.log1p(pressure * 3);
}

export function toLinearSpace(logValue: number): number {
    return (Math.expm1(logValue)) / 3;
}

/**
 * Maximum gap between points to be considered in the same cluster (in hours)
 * Points within 2-4 hours of each other will be clustered together
 */
export const CLUSTER_MAX_GAP_HOURS = 3;

/**
 * Cluster nearby points and average x and y values within each cluster
 * Groups data points into clusters where consecutive points are within CLUSTER_MAX_GAP_HOURS
 * @param xValues Array of x values (in days)
 * @param yValues Array of y values
 * @param maxGapHours Maximum gap in hours to consider points in the same cluster (default: CLUSTER_MAX_GAP_HOURS)
 * @returns Object with averaged xValues and yValues arrays (averaged x and y for each cluster)
 */
export function averageTimeBlocks(
    xValues: number[],
    yValues: number[],
    maxGapHours: number = CLUSTER_MAX_GAP_HOURS
): { xValues: number[]; yValues: number[] } {
    if (xValues.length !== yValues.length || xValues.length === 0) {
        return { xValues: [...xValues], yValues: [...yValues] };
    }

    // Create array of {x, y} pairs and sort by x
    const points = xValues.map((x, i) => ({ x, y: yValues[i] }));
    points.sort((a, b) => a.x - b.x);

    const maxGapDays = maxGapHours / 24;
    const clusters: Array<Array<{ x: number; y: number }>> = [];
    let currentCluster: Array<{ x: number; y: number }> = [points[0]];

    // Group points into clusters based on time gaps
    for (let i = 1; i < points.length; i++) {
        const gap = points[i].x - points[i - 1].x;

        if (gap <= maxGapDays) {
            // Add to current cluster
            currentCluster.push(points[i]);
        } else {
            // Start a new cluster
            clusters.push(currentCluster);
            currentCluster = [points[i]];
        }
    }
    // Don't forget the last cluster
    clusters.push(currentCluster);

    // Calculate average x and y for each cluster
    const averagedX: number[] = [];
    const averagedY: number[] = [];

    clusters.forEach((cluster) => {
        const avgX = cluster.reduce((sum, p) => sum + p.x, 0) / cluster.length;
        const avgY = cluster.reduce((sum, p) => sum + p.y, 0) / cluster.length;

        averagedX.push(avgX);
        averagedY.push(avgY);
    });

    return { xValues: averagedX, yValues: averagedY };
}

/**
 * Polynomial regression using least squares
 * Returns coefficients for polynomial: y = a0 + a1*x + a2*x^2 + ... + an*x^n
 * @param xValues Array of x values
 * @param yValues Array of y values
 * @param order Polynomial order (e.g., 2 for quadratic, 3 for cubic)
 * @returns Array of coefficients [a0, a1, a2, ..., an]
 */
export function polynomialRegression(xValues: number[], yValues: number[], order = 2): number[] {
    if (xValues.length !== yValues.length || xValues.length < order + 1) {
        return [];
    }

    const n = xValues.length;
    const m = order + 1;

    // Build the Vandermonde matrix
    const A: number[][] = [];
    for (let i = 0; i < n; i++) {
        const row: number[] = [];
        for (let j = 0; j < m; j++) {
            row.push(xValues[i] ** j);
        }
        A.push(row);
    }

    // Build the right-hand side vector
    const b: number[] = yValues;

    // Solve using least squares: (A^T * A) * x = A^T * b
    // Compute A^T * A
    const AtA: number[][] = [];
    for (let i = 0; i < m; i++) {
        const row: number[] = [];
        for (let j = 0; j < m; j++) {
            let sum = 0;
            for (let k = 0; k < n; k++) {
                sum += A[k][i] * A[k][j];
            }
            row.push(sum);
        }
        AtA.push(row);
    }

    // Compute A^T * b
    const Atb: number[] = [];
    for (let i = 0; i < m; i++) {
        let sum = 0;
        for (let k = 0; k < n; k++) {
            sum += A[k][i] * b[k];
        }
        Atb.push(sum);
    }

    // Solve the system using Gaussian elimination
    const augmented: number[][] = AtA.map((row, i) => [...row, Atb[i]]);

    // Forward elimination
    for (let i = 0; i < m; i++) {
        // Find pivot
        let maxRow = i;
        for (let k = i + 1; k < m; k++) {
            if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
                maxRow = k;
            }
        }
        [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

        // Make all rows below this one 0 in current column
        for (let k = i + 1; k < m; k++) {
            const factor = augmented[k][i] / augmented[i][i];
            for (let j = i; j < m + 1; j++) {
                augmented[k][j] -= factor * augmented[i][j];
            }
        }
    }

    // Back substitution
    const coefficients: number[] = new Array(m).fill(0);
    for (let i = m - 1; i >= 0; i--) {
        coefficients[i] = augmented[i][m];
        for (let j = i + 1; j < m; j++) {
            coefficients[i] -= augmented[i][j] * coefficients[j];
        }
        coefficients[i] /= augmented[i][i];
    }

    return coefficients;
}

/**
 * Evaluate polynomial at given x value
 */
export function evaluatePolynomial(coefficients: number[], x: number): number {
    let result = 0;
    for (let i = 0; i < coefficients.length; i++) {
        result += coefficients[i] * (x ** i);
    }
    return result;
}

